import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  Route as RouteIcon,
  Today as TodayIcon,
  Work as WorkIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  People as CrewIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  AccessTime as ClockIcon,
  Map as MapIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MapboxFleetMap from '../Maps/MapboxFleetMap';
import { makeApiCall } from '../../config/api';

const DAYS_OF_WEEK = [
  { value: 1, key: 'monday' },
  { value: 2, key: 'tuesday' },
  { value: 3, key: 'wednesday' },
  { value: 4, key: 'thursday' },
  { value: 5, key: 'friday' },
  { value: 6, key: 'saturday' },
  { value: 7, key: 'sunday' },
];

/**
 * OperationsDashboard - the "good morning" landing page for managers.
 *
 * One screen with everything a dispatcher needs at 7 AM:
 *   - Today's scheduled/in-progress/completed counts
 *   - Live truck positions on a map
 *   - Today's routes (truck x stop count x crew) with quick actions
 *   - Attention-needed panel (trucks without drivers, off-schedule work,
 *     overtime visits, un-materialized routes)
 *   - Jump-to shortcuts for the usual screens
 *
 * This page auto-refreshes every 60 seconds so numbers stay honest without
 * a reload.
 */
const OperationsDashboard = ({ onNavigatePage }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [trucks, setTrucks] = useState([]);
  const [truckLocations, setTruckLocations] = useState([]);
  const [todayCounts, setTodayCounts] = useState({});
  const [slotCounts, setSlotCounts] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  const todayDow = (() => {
    const d = new Date().getDay();
    return d === 0 ? 7 : d;
  })();
  const todayKey = DAYS_OF_WEEK.find((d) => d.value === todayDow)?.key || 'monday';

  // Local-timezone YYYY-MM-DD. Avoid `toISOString().split('T')[0]` here - that
  // returns UTC, which flips to tomorrow after ~8pm ET and makes every
  // today-oriented count on this page count the wrong day's work.
  const todayIsoLocal = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  const fetchAll = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError('');

      const [statsRes, trucksRes, jobSitesRes, todayRes, gpsRes] = await Promise.all([
        makeApiCall(`api/jobs/stats/dashboard?date=${todayIsoLocal}`),
        makeApiCall('/api/routes/trucks'),
        makeApiCall('/api/routes/job-sites?is_active=true'),
        makeApiCall(`/api/routes/today-active-counts?date=${todayIsoLocal}`),
        makeApiCall('api/gps/locations/current').catch(() => null),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (trucksRes.ok) {
        const data = await trucksRes.json();
        setTrucks((data.trucks || []).filter((tr) => tr.is_active !== false));
      }
      if (jobSitesRes.ok) {
        const data = await jobSitesRes.json();
        const counts = {};
        (data.job_sites || []).forEach((js) => {
          const day = js.is_custom ? js.custom_day_of_week : js.day_of_week;
          const truck = js.is_custom ? js.custom_truck_id : js.truck_id;
          if (!day || !truck) return;
          const key = `${day}:${truck}`;
          counts[key] = (counts[key] || 0) + 1;
        });
        setSlotCounts(counts);
      }
      if (todayRes && todayRes.ok) {
        const data = await todayRes.json();
        setTodayCounts(data.counts || {});
      }
      if (gpsRes && gpsRes.ok) {
        const data = await gpsRes.json();
        setTruckLocations(data.truck_locations || []);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('OperationsDashboard load failed:', err);
      setError(t('operations.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t, todayIsoLocal]);

  useEffect(() => {
    fetchAll(false);
    const handle = setInterval(() => fetchAll(true), 60_000);
    return () => clearInterval(handle);
  }, [fetchAll]);

  // Build today's truck rows: only the ones with planned or actual work today.
  const todayRows = trucks
    .map((truck) => {
      const planned = Number(slotCounts[`${todayDow}:${truck.id}`] || 0);
      const breakdown = todayCounts[truck.id] || {};
      const total = Number(breakdown.total || 0) || planned;
      const offSchedule = Number(breakdown.offSchedule || 0);
      const completed = Number(breakdown.completed || 0);
      const inProgress = Number(breakdown.inProgress || 0);
      const location = truckLocations.find((tl) => tl.truck_id === truck.id);
      return { truck, planned, total, offSchedule, completed, inProgress, location };
    })
    .filter((r) => r.planned > 0 || r.total > 0)
    .sort((a, b) => String(a.truck.name || '').localeCompare(String(b.truck.name || '')));

  // Attention items
  const attentionItems = [];
  // Trucks with planned work today but no driver assigned
  for (const { truck, planned } of todayRows) {
    if (planned > 0 && !truck.current_driver_user_id) {
      attentionItems.push({
        key: `no-driver-${truck.id}`,
        severity: 'warning',
        message: t('operations.attentionNoDriver', { truck: truck.name }),
        action: { label: t('operations.openRoutes'), page: 'routes-list' },
      });
    }
  }
  // Trucks with today's jobs that are off-schedule
  for (const { truck, offSchedule } of todayRows) {
    if (offSchedule > 0) {
      attentionItems.push({
        key: `off-schedule-${truck.id}`,
        severity: 'info',
        message: t('operations.attentionOffSchedule', { truck: truck.name, count: offSchedule }),
        action: { label: t('operations.openJobs'), page: 'jobs' },
      });
    }
  }
  // Overtime GPS visits
  const overtimeTrucks = truckLocations.filter((tl) => tl.gps_status === 'overtime');
  for (const tl of overtimeTrucks) {
    attentionItems.push({
      key: `overtime-${tl.truck_id}`,
      severity: 'error',
      message: t('operations.attentionOvertime', { truck: tl.truck_name }),
      action: { label: t('operations.openLiveTracking'), page: 'live-tracking' },
    });
  }

  const scheduledToday = stats.scheduled_today ?? 0;
  const completedToday = stats.completed_today ?? 0;
  const inProgressNow = stats.in_progress_now ?? 0;
  const remainingToday = stats.remaining_today ?? Math.max(0, scheduledToday - completedToday);
  const progressPct = scheduledToday > 0 ? Math.round((completedToday / scheduledToday) * 100) : 0;

  const jumpTo = (page) => {
    if (onNavigatePage) onNavigatePage(page);
  };

  const openPrint = (truckId) => {
    window.open(`/schedule/print?day_of_week=${todayDow}&truck_id=${truckId}`, '_blank');
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-demo-target="ops-root">
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
        data-demo-target="ops-header"
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TodayIcon color="primary" />
            {t('operations.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString(undefined, {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
            {lastUpdated && ` · ${t('operations.updatedAt', {
              time: lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            })}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('operations.refresh')}>
            <IconButton
              onClick={() => fetchAll(true)}
              disabled={refreshing}
              aria-label={t('operations.refresh')}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {(loading || refreshing) && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Counters */}
      <Grid container spacing={2} sx={{ mb: 2 }} data-demo-target="ops-kpi-cards">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} data-demo-target="ops-card-scheduled">
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <WorkIcon color="info" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('operations.scheduledToday')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {scheduledToday}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} data-demo-target="ops-card-in-progress">
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ClockIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('operations.inProgressNow')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {inProgressNow}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} data-demo-target="ops-card-completed">
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CompleteIcon color="success" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('operations.completedToday')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {completedToday}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} data-demo-target="ops-card-progress">
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('operations.dayProgress')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {progressPct}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('operations.remainingCount', { count: remainingToday })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Attention Needed */}
      {attentionItems.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-demo-target="ops-attention">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            <WarningIcon
              sx={{ color: 'warning.main', verticalAlign: 'middle', mr: 0.5, fontSize: 20 }}
            />
            {t('operations.attentionNeeded', { count: attentionItems.length })}
          </Typography>
          <Stack spacing={1}>
            {attentionItems.map((item) => (
              <Alert
                key={item.key}
                severity={item.severity}
                action={
                  item.action ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => jumpTo(item.action.page)}
                    >
                      {item.action.label}
                    </Button>
                  ) : null
                }
              >
                {item.message}
              </Alert>
            ))}
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        {/* Live fleet map */}
        <Grid size={{ xs: 12, lg: 6 }} data-demo-target="ops-fleet-map">
          <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 440 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                <MapIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                {t('operations.liveFleet')}
              </Typography>
              <Button
                size="small"
                onClick={() => jumpTo('live-tracking')}
                endIcon={<OpenInNewIcon fontSize="small" />}
                aria-label={t('operations.fullTracking')}
              >
                {t('operations.fullTracking')}
              </Button>
            </Stack>
            <Box sx={{ height: 380 }}>
              {(() => {
                // Only show GPS positions for trucks that actually have work
                // today. The full-fleet view lives on the Live Tracking page.
                const activeTruckIds = new Set(todayRows.map((r) => r.truck.id));
                const activeLocations = truckLocations.filter((tl) =>
                  activeTruckIds.has(tl.truck_id)
                );
                if (activeLocations.length > 0) {
                  return <MapboxFleetMap trucks={activeLocations} height="100%" />;
                }
                if (truckLocations.length > 0) {
                  return (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t('operations.noActiveTrucksOnMap')}
                    </Alert>
                  );
                }
                return (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('operations.noGpsYet')}
                  </Alert>
                );
              })()}
            </Box>
          </Paper>
        </Grid>

        {/* Today's routes */}
        <Grid size={{ xs: 12, lg: 6 }} data-demo-target="ops-routes-table">
          <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 440 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                <RouteIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                {t('operations.todaysRoutes', { day: t(`days.${todayKey}`) })}
              </Typography>
              <Button
                size="small"
                onClick={() => jumpTo('routes-list')}
                endIcon={<OpenInNewIcon fontSize="small" />}
              >
                {t('operations.allRoutes')}
              </Button>
            </Stack>

            {todayRows.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('operations.noRoutesToday')}
              </Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('operations.truck')}</strong></TableCell>
                      <TableCell><strong>{t('operations.crew')}</strong></TableCell>
                      <TableCell><strong>{t('operations.progress')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('operations.actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayRows.map(({ truck, total, offSchedule, completed, inProgress, location }) => {
                      const driver = truck.driver_first_name || truck.driver_last_name
                        ? `${truck.driver_first_name || ''} ${truck.driver_last_name || ''}`.trim()
                        : null;
                      const passenger = truck.passenger_first_name || truck.passenger_last_name
                        ? `${truck.passenger_first_name || ''} ${truck.passenger_last_name || ''}`.trim()
                        : null;
                      const gpsOn = location && location.ignition_status === 'On';
                      const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                      const allDone = total > 0 && completed >= total;
                      const barColor = allDone ? 'success' : inProgress > 0 ? 'primary' : 'info';
                      return (
                        <TableRow key={truck.id} hover data-demo-target="ops-routes-row">
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <TruckIcon
                                fontSize="small"
                                sx={{
                                  color: gpsOn ? 'success.main' : 'text.disabled',
                                }}
                              />
                              <strong>{truck.name}</strong>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {driver ? (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <CrewIcon fontSize="small" />
                                <Typography variant="body2" component="span">
                                  {driver}
                                  {passenger && ` + ${passenger}`}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="warning.main">
                                {t('operations.noDriver')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Stack spacing={0.25}>
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  {completed}/{total}
                                </Typography>
                                {inProgress > 0 && (
                                  <Chip
                                    label={t('operations.inProgressChip', { count: inProgress })}
                                    size="small"
                                    color="primary"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                {allDone && (
                                  <Chip
                                    label={t('operations.doneChip')}
                                    size="small"
                                    color="success"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                {offSchedule > 0 && (
                                  <Chip
                                    label={`+${offSchedule} ${t('operations.offSchedule')}`}
                                    size="small"
                                    color="warning"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                color={barColor}
                                sx={{ height: 6, borderRadius: 3 }}
                                aria-label={t('operations.progressAria', { completed, total })}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('operations.printSchedule')}>
                              <IconButton
                                size="small"
                                onClick={() => openPrint(truck.id)}
                                aria-label={t('operations.printSchedule')}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick links */}
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-demo-target="ops-quick-links">
        <Button variant="outlined" onClick={() => jumpTo('route-setup')} startIcon={<RouteIcon />}>
          {t('operations.quickRoutePlanning')}
        </Button>
        <Button variant="outlined" onClick={() => jumpTo('routes-list')} startIcon={<RouteIcon />}>
          {t('operations.quickRoutesList')}
        </Button>
        <Button variant="outlined" onClick={() => jumpTo('jobs')} startIcon={<WorkIcon />}>
          {t('operations.quickJobs')}
        </Button>
        <Button variant="outlined" onClick={() => jumpTo('quotes')}>
          {t('operations.quickQuotes')}
        </Button>
        <Button variant="outlined" onClick={() => jumpTo('customers')}>
          {t('operations.quickCustomers')}
        </Button>
      </Stack>
    </Box>
  );
};

export default OperationsDashboard;
